import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Box, Button, Stack, Typography } from '@mui/material';
import { BOXES } from '../../config/infra';
import type { BoxStatus } from '../../api/statusApi';
import type { ConvmemStatus } from '../../api/convmemApi';
import { fetchOverview } from '../../api/overviewApi';
import BoxCard from '../overview/BoxCard';
import ArchitectureCard from '../overview/ArchitectureCard';
import RootFsCard from '../overview/RootFsCard';
import DriveLayoutSection from '../overview/DriveLayoutSection';
import ConvmemCard from '../overview/ConvmemCard';

const REFRESH_MS = 15000;

type StatusMap = Record<string, BoxStatus | undefined>;

const BOX_IDS = BOXES.map(b => b.id);

const OverviewTab: React.FC = () => {
    const auth = useAuth();
    const token = auth.user?.access_token ?? '';
    const [statuses, setStatuses] = useState<StatusMap>({});
    const [convmem, setConvmem] = useState<ConvmemStatus | undefined>(undefined);
    const [refreshing, setRefreshing] = useState(false);
    const mounted = useRef(true);

    // Single request to the portal API's GET /overview (HOME-243) replaces
    // what used to be one browser-side fetch per box plus one for convmem —
    // the facade does that fan-out itself, server-side, over mTLS.
    const refresh = useCallback(async () => {
        if (!token) return;
        setRefreshing(true);
        const { boxes, convmem: convmemStatus } = await fetchOverview(token, BOX_IDS);
        if (!mounted.current) return;
        setStatuses(boxes);
        setConvmem(convmemStatus);
        setRefreshing(false);
    }, [token]);

    useEffect(() => {
        mounted.current = true;
        void refresh();
        const id = setInterval(() => { void refresh(); }, REFRESH_MS);
        return () => { mounted.current = false; clearInterval(id); };
    }, [refresh]);

    return (
        <Box>
            <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 0.5 }}>
                <Typography variant='h5'>What lives on which box</Typography>
                <Button variant='outlined' onClick={() => void refresh()} disabled={refreshing}>
                    {refreshing ? 'Refreshing…' : 'Refresh'}
                </Button>
            </Stack>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Read-only view of the home-infra boxes and their expected containers.
                Auto-refreshes every {REFRESH_MS / 1000}s.
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: 2,
                    alignItems: 'stretch',
                }}
            >
                {BOXES.map(b => (
                    <BoxCard key={b.id} box={b} status={statuses[b.id]} />
                ))}
            </Box>

            <Box sx={{ mt: 2 }}>
                <ConvmemCard status={convmem} />
            </Box>

            <Box sx={{ mt: 2 }}>
                <ArchitectureCard />
            </Box>

            <Box sx={{ mt: 2 }}>
                <RootFsCard />
            </Box>

            <Box sx={{ mt: 2 }}>
                <DriveLayoutSection />
            </Box>
        </Box>
    );
};

export default OverviewTab;
